/**
 * Mint, copy, and revoke the links that make one artifact readable without an
 * account.
 *
 * Sharing is explicit here on purpose: nothing mints a link when a briefing
 * runs, because that would give every briefing ever produced a live public
 * token. The user asks, once, per recipient.
 *
 * "Per recipient" is why `recipient` is a field rather than a single link per
 * artifact — one link each is what makes a view count mean something, and it is
 * the shape a subscriber list drops straight into later.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import { copyText } from '../../utils/clipboard';
import type { ArtifactKind, ShareLink } from '../../types';

/** Panel width; must match the `w-96` below, since placement is computed in px. */
const PANEL_W = 384;
const GAP = 8;
const EDGE = 8;

/**
 * The owner's own look at a link, marked so the reader app skips its view
 * beacon.
 *
 * Without it, checking what you just sent lands in the recipient's view count —
 * and that count is the only thing telling the owner whether the link was
 * actually read. Copy still copies the clean URL; the marker exists only on the
 * button that opens it from in here.
 */
function previewUrl(url: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}preview=1`;
}

interface Position {
  left: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
}

interface Props {
  kind: ArtifactKind;
  newsBriefingId?: number;
  managedPageId?: number;
  boardroomId?: number;
  /** One report or table from a room (kind BOARDROOM_CARD). */
  boardroomDeliverableId?: number;
  className?: string;
  /** Under the list: more of this thing's share history, elsewhere (a room's
   *  single views). Handed a way to close the panel first. */
  footer?: (close: () => void) => ReactNode;
}

export default function ShareArtifactControl({
  kind,
  newsBriefingId,
  managedPageId,
  boardroomId,
  boardroomDeliverableId,
  className = '',
  footer,
}: Props) {
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState<ShareLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  // The link being renamed, and the text in the box. A link's name is its
  // `recipient` — the same field the Create box sets, editable afterwards,
  // because "who is this for" is usually decided after the link is minted.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  // Escape has to leave the field WITHOUT saving, and both Escape and Enter
  // leave by blurring, so the blur handler needs to know which one sent it.
  const cancelledRef = useRef(false);
  const [pos, setPos] = useState<Position | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setLinks(
        await api.listShareLinks({
          kind,
          newsBriefingId,
          managedPageId,
          boardroomId,
          boardroomDeliverableId,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load links');
    } finally {
      setLoading(false);
    }
  }, [kind, newsBriefingId, managedPageId, boardroomId, boardroomDeliverableId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  /**
   * The panel is a portal pinned to the button, not an absolutely-positioned
   * child.
   *
   * As a child it was clipped: the artifact shelf wraps each group in a
   * `overflow-hidden` list (that is what rounds the corners of the rows), so an
   * open Share panel got sliced off at the bottom of a short list. Any ancestor
   * with overflow or its own stacking context would do the same, and there are
   * three unrelated screens mounting this control. Escaping the tree fixes all
   * of them at once, at the price of computing the coordinates here.
   */
  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const left = Math.max(EDGE, Math.min(r.right - PANEL_W, window.innerWidth - PANEL_W - EDGE));
    const below = window.innerHeight - r.bottom - GAP - EDGE;
    const above = r.top - GAP - EDGE;
    // Drop down unless there is meaningfully more room above.
    if (below >= 260 || below >= above) setPos({ left, top: r.bottom + GAP, maxHeight: below });
    else setPos({ left, bottom: window.innerHeight - r.top + GAP, maxHeight: above });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    place();
    window.addEventListener('resize', place);
    // Capture phase: the anchor may sit inside a scrolling pane, not just the page.
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  // Click-away. Without it the panel stays open behind whatever you click next.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const mint = async () => {
    setError(null);
    try {
      const link = await api.createShareLink({
        kind,
        news_briefing_id: newsBriefingId,
        managed_page_id: managedPageId,
        boardroom_id: boardroomId,
        boardroom_deliverable_id: boardroomDeliverableId,
        recipient: recipient.trim() || undefined,
      });
      setRecipient('');
      setLinks((prev) => [link, ...prev]);
      await copy(link);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create link');
    }
  };

  const copy = async (link: ShareLink) => {
    if (await copyText(link.url)) {
      setError(null);
      setCopiedId(link.id);
      setTimeout(() => setCopiedId((id) => (id === link.id ? null : id)), 1800);
      return;
    }
    // Every path refused. Say so — the URL is printed under the row, so there
    // is something to select by hand.
    setError('Could not reach the clipboard. Select the URL below and copy it.');
  };

  const rename = async (link: ShareLink) => {
    const next = draft.trim();
    if (next === (link.recipient ?? '')) return;
    setError(null);
    try {
      const updated = await api.updateShareLink(link.id, { recipient: next });
      setLinks((prev) => prev.map((l) => (l.id === link.id ? updated : l)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not rename');
    }
  };

  const revoke = async (link: ShareLink) => {
    setError(null);
    try {
      await api.revokeShareLink(link.id);
      setLinks((prev) => prev.filter((l) => l.id !== link.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not revoke');
    }
  };

  return (
    <div className={className}>
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="text-xs px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
      >
        Share{links.length > 0 ? ` (${links.length})` : ''}
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            /* A portal still bubbles its events through the REACT tree, so the
               host row's click handler would see them — on the shelf that
               handler preventDefaults, which would swallow the Open link. */
            onClick={(e) => e.stopPropagation()}
            style={{
              left: pos.left,
              top: pos.top,
              bottom: pos.bottom,
              maxHeight: Math.max(pos.maxHeight, 160),
            }}
            className="fixed z-50 w-96 flex flex-col overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg p-3"
          >
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Anyone with the link can read this - no account needed. It won’t be indexed by
              search engines.
            </p>

            <div className="flex gap-2 mb-3">
              <input
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') mint();
                }}
                placeholder="Who is this for? (optional)"
                className="flex-1 min-w-0 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-1"
              />
              <button
                onClick={mint}
                className="text-sm px-3 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700 shrink-0"
              >
                Create
              </button>
            </div>

            {error && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{error}</p>}
            {loading && <p className="text-xs text-gray-400">Loading…</p>}

            {/* THE SHARE HISTORY, said as one: every link made here, who it is
                for and whether it was read. */}
            {!loading && links.length > 0 && (
              <h4 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Shared so far
              </h4>
            )}
            {!loading && links.length === 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500">Not shared yet.</p>
            )}

            <ul className="flex flex-col gap-2">
              {links.map((link) => (
                <li
                  key={link.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-md px-2.5 py-2"
                >
                  <div className="flex items-center gap-2">
                    {editingId === link.id ? (
                      <input
                        autoFocus
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === 'Escape') {
                            e.preventDefault();
                            cancelledRef.current = e.key === 'Escape';
                            e.currentTarget.blur();
                          }
                        }}
                        onBlur={() => {
                          const cancelled = cancelledRef.current;
                          cancelledRef.current = false;
                          setEditingId(null);
                          if (!cancelled) rename(link);
                        }}
                        placeholder="Who is this for?"
                        className="flex-1 min-w-0 text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-1.5 py-0.5"
                      />
                    ) : (
                      <button
                        onClick={() => {
                          setDraft(link.recipient ?? '');
                          setEditingId(link.id);
                        }}
                        title="Click to say who this link is for"
                        className={`text-xs font-medium truncate flex-1 text-left rounded px-1 -mx-1 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          link.recipient
                            ? 'text-gray-700 dark:text-gray-200'
                            : 'text-gray-400 dark:text-gray-500 italic'
                        }`}
                      >
                        {/* The empty state has to name the SAME field the box
                            above asks for. "Untitled link" invented a second
                            idea — a title — for what is a recipient, so the
                            row and the prompt that fills it read as two
                            different things. */}
                        {link.recipient || 'No recipient yet'}
                      </button>
                    )}
                    <a
                      href={previewUrl(link.url)}
                      target="_blank"
                      rel="noreferrer"
                      title="Open the reader's page - your own look is not counted as a view"
                      className="text-xs underline hover:no-underline text-blue-600 dark:text-blue-400 shrink-0"
                    >
                      Open
                    </a>
                    <button
                      onClick={() => copy(link)}
                      className="text-xs underline hover:no-underline text-blue-600 dark:text-blue-400 shrink-0"
                    >
                      {copiedId === link.id ? 'Copied' : 'Copy'}
                    </button>
                    <button
                      onClick={() => revoke(link)}
                      className="text-xs underline hover:no-underline text-red-600 dark:text-red-400 shrink-0"
                    >
                      Revoke
                    </button>
                  </div>
                  {/* The URL itself, printed rather than implied: it is what
                      Copy puts on the clipboard, and `select-all` makes one
                      click enough to take it by hand when Copy cannot. */}
                  <p className="text-[11px] font-mono text-gray-500 dark:text-gray-400 mt-1 break-all select-all">
                    {link.url}
                  </p>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                    {link.view_count} view{link.view_count === 1 ? '' : 's'}
                    {link.last_viewed_at &&
                      ` · last ${new Date(link.last_viewed_at).toLocaleDateString()}`}
                  </p>
                </li>
              ))}
            </ul>
            {footer && (
              <div className="mt-3 border-t border-gray-200 pt-2 dark:border-gray-700">{footer(() => setOpen(false))}</div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
